import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import {
  downloadMingchuanQrCode,
  MINGCHUAN_QR_IMAGE,
  MINGCHUAN_WEBSITE_URL,
} from '../utils/downloadMingchuanQr';

const BTN_DOWNLOAD_BG = '#999999';
const BTN_OK_BG = '#d4af37';

export default function InviteWebsiteQrModal({ visible, onClose }) {
  const theme = useTheme();
  const gold = theme.colors.primary || '#d4af37';
  const styles = getStyles(theme, gold);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadMingchuanQrCode();
    } catch (err) {
      Alert.alert('Download failed', err?.message || 'Could not download the QR code.');
    } finally {
      setDownloading(false);
    }
  };

  const sheet = (
    <Pressable
      style={styles.sheet}
      onPress={(e) => e.stopPropagation?.()}
      nativeID="invite-website-modal-sheet"
      {...(Platform.OS === 'web' ? { dataSet: { inviteWebsiteModalSheet: 'true' } } : {})}
    >
      <TouchableOpacity
        style={styles.closeBtn}
        onPress={onClose}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel="Close"
      >
        <MaterialIcons name="close" size={24} color={theme.colors.text} />
      </TouchableOpacity>

      <Text style={styles.title}>Invite to Website</Text>
      <Text style={styles.subtitle}>
        Scan/download to share the QR code for the Ming Ch'uan website.
      </Text>

      <View style={styles.qrWrap}>
        <Image source={MINGCHUAN_QR_IMAGE} style={styles.qrImage} contentFit="contain" />
      </View>

      <Text style={styles.urlText}>{MINGCHUAN_WEBSITE_URL}</Text>

      <View
        style={styles.btnRow}
        nativeID="invite-website-modal-btn-row"
        {...(Platform.OS === 'web' ? { dataSet: { inviteWebsiteModalBtnRow: 'true' } } : {})}
      >
        <View
          style={styles.downloadBtnShell}
          nativeID="invite-website-modal-download-btn"
          {...(Platform.OS === 'web' ? { dataSet: { inviteWebsiteModalDownloadBtn: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.downloadBtnInner}
            onPress={handleDownload}
            disabled={downloading}
            activeOpacity={0.85}
          >
            {downloading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text style={styles.downloadBtnText}>Download</Text>
            )}
          </TouchableOpacity>
        </View>
        <View
          style={styles.okBtnShell}
          nativeID="invite-website-modal-ok-btn"
          {...(Platform.OS === 'web' ? { dataSet: { inviteWebsiteModalOkBtn: 'true' } } : {})}
        >
          <TouchableOpacity style={styles.okBtnInner} onPress={onClose} activeOpacity={0.85}>
            <Text style={styles.okBtnText}>OK</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Pressable>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={styles.modalRoot}
        nativeID="invite-website-modal-root"
        {...(Platform.OS === 'web' ? { dataSet: { inviteWebsiteModalRoot: 'true' } } : {})}
      >
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close invite dialog"
          nativeID="invite-website-modal-dismiss"
          {...(Platform.OS === 'web' ? { dataSet: { inviteWebsiteModalDismiss: 'true' } } : {})}
        />
        <View style={styles.backdropCenter} pointerEvents="box-none">
          {sheet}
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme, gold) {
  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
    },
    backdropCenter: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      backgroundColor: 'transparent',
    },
    sheet: {
      width: '100%',
      maxWidth: 360,
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      paddingTop: 44,
      borderWidth: 2,
      borderColor: gold,
      alignItems: 'center',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 8,
          }),
    },
    closeBtn: {
      position: 'absolute',
      top: 10,
      right: 10,
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      backgroundColor: 'transparent',
    },
    title: {
      fontSize: 18,
      fontWeight: '700',
      color: gold,
      marginBottom: 8,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 14,
      color: theme.colors.text,
      marginBottom: 16,
      textAlign: 'center',
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    qrWrap: {
      padding: 12,
      backgroundColor: '#FFFFFF',
      borderRadius: 8,
      marginBottom: 12,
    },
    qrImage: {
      width: 220,
      height: 220,
    },
    urlText: {
      fontSize: 14,
      color: theme.colors.text,
      marginBottom: 20,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    btnRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      backgroundColor: 'transparent',
    },
    downloadBtnShell: {
      backgroundColor: BTN_DOWNLOAD_BG,
      borderRadius: 8,
      minWidth: 120,
      overflow: 'hidden',
    },
    downloadBtnInner: {
      backgroundColor: 'transparent',
      paddingVertical: 12,
      paddingHorizontal: 22,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
    },
    downloadBtnText: {
      fontSize: 16,
      fontWeight: '600',
      color: '#FFFFFF',
      backgroundColor: 'transparent',
    },
    okBtnShell: {
      backgroundColor: BTN_OK_BG,
      borderRadius: 8,
      minWidth: 120,
      overflow: 'hidden',
    },
    okBtnInner: {
      backgroundColor: 'transparent',
      paddingVertical: 12,
      paddingHorizontal: 22,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
    },
    okBtnText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000000',
      backgroundColor: 'transparent',
    },
  });
}
